import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Hotel, Calendar, ShoppingBag, Car, Building2, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getWhatsAppLink } from "@/data/sample-listings";

const verticals = [
  {
    icon: Hotel,
    title: "Hotels & Resorts",
    description: "Immersive suite-by-suite walkthroughs that let guests explore before booking.",
    category: "hotel",
    color: "from-secondary/20 to-secondary/5",
    borderColor: "border-secondary/20",
  },
  {
    icon: Calendar,
    title: "Event Centres",
    description: "Showcase capacity, décor, and ambiance with full 360° venue tours.",
    category: "event-centre",
    color: "from-primary/20 to-primary/5",
    borderColor: "border-primary/20",
  },
  {
    icon: ShoppingBag,
    title: "Malls & Showrooms",
    description: "Let shoppers explore retail spaces and product displays virtually.",
    category: "office",
    color: "from-accent/20 to-accent/5",
    borderColor: "border-accent/20",
  },
  {
    icon: Car,
    title: "Auto Showrooms",
    description: "360° showroom tours for dealerships — every angle, every model.",
    category: "office",
    color: "from-secondary/20 to-secondary/5",
    borderColor: "border-secondary/20",
  },
  {
    icon: Building2,
    title: "Mixed-Use Estates",
    description: "Map entire gated communities with Vista Street View — roads, amenities, and plots.",
    category: "apartment",
    color: "from-primary/20 to-primary/5",
    borderColor: "border-primary/20",
  },
];

export const BusinessSolutions = () => {
  return (
    <section className="section-padding bg-muted/50">
      <div className="container-tight">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-primary font-semibold text-sm uppercase tracking-wider"
          >
            Enterprise Verticals
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-4"
          >
            Business Solutions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-lg"
          >
            Tailored 8K virtual tour packages for every industry — from hospitality to real estate development.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {verticals.map((v, index) => {
            const whatsappMsg = `Hello Vista360! I'm interested in the 8K Capture service for my ${v.title.toLowerCase()}. Please share package details and scheduling options.`;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className={`bg-card rounded-2xl p-6 border ${v.borderColor} backdrop-blur-sm card-hover`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${v.color} flex items-center justify-center mb-4`}>
                  <v.icon className="w-6 h-6 text-foreground" />
                </div>
                <h3 className="font-display font-bold text-lg text-foreground mb-2">{v.title}</h3>
                <p className="text-muted-foreground text-sm mb-5">{v.description}</p>
                <a
                  href={getWhatsAppLink(whatsappMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Request 8K Capture
                  <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-10 text-center"
        >
          <Link to="/pricing">
            <Button size="lg" variant="outline" className="font-semibold">
              View All Service Tiers
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
